import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import Pagination from '../components/Pagination';

const ApplyLoan = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    pages: 1,
  });
  const [formData, setFormData] = useState({
    requestedAmount: '',
    loanType: '',
    purpose: '',
    requestedTermMonths: '3',
    collateralType: 'CRYPTOCURRENCY',
  });

  // API constraints
  const MIN_LOAN_AMOUNT = 100;
  const MAX_LOAN_AMOUNT = 1000000;
  const MAX_LOAN_TERM_MONTHS = 60;
  const MIN_PURPOSE_LENGTH = 10;
  const MAX_PURPOSE_LENGTH = 500;

  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Fetch loan applications
  const fetchApplications = async (page = 1) => {
    setApplicationsLoading(true);
    try {
      const response = await api.get(
        `/api/loans/applications?page=${page}&limit=5`
      );
      if (response.data.success) {
        setApplications(response.data.data.applications || []);
        setPagination(
          response.data.data.pagination || {
            page: 1,
            limit: 10,
            total: 0,
            pages: 1,
          }
        );
      }
    } catch (error) {
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchApplications(newPage);
    }
  };

  // Load applications on mount
  React.useEffect(() => {
    fetchApplications();
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Format requestedAmount to 2 decimal places
    if (name === 'requestedAmount') {
      // Allow empty or valid number input
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAmountBlur = () => {
    if (formData.requestedAmount && !isNaN(formData.requestedAmount)) {
      const numValue = parseFloat(formData.requestedAmount);
      const formatted = numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      setFormData((prev) => ({
        ...prev,
        requestedAmount: formatted,
      }));
    }
  };

  const handleAmountFocus = () => {
    if (formData.requestedAmount) {
      // Remove commas when focusing for easier editing
      const unformatted = formData.requestedAmount.replace(/,/g, '');
      setFormData((prev) => ({
        ...prev,
        requestedAmount: unformatted,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !formData.requestedAmount ||
      !formData.loanType ||
      !formData.purpose ||
      !formData.collateralType
    ) {
      toast.error('Please fill in all required fields', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    // Validate purpose length
    if (formData.purpose.length < MIN_PURPOSE_LENGTH) {
      toast.error(`Purpose must be at least ${MIN_PURPOSE_LENGTH} characters`, {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    if (formData.purpose.length > MAX_PURPOSE_LENGTH) {
      toast.error(`Purpose must not exceed ${MAX_PURPOSE_LENGTH} characters`, {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    // Remove commas before parsing
    const amountString = formData.requestedAmount.replace(/,/g, '');
    const amount = parseFloat(amountString);
    if (isNaN(amount) || amount < MIN_LOAN_AMOUNT) {
      toast.error(`Minimum loan amount is $${formatNumber(MIN_LOAN_AMOUNT)}`, {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    if (amount > MAX_LOAN_AMOUNT) {
      toast.error(`Maximum loan amount is $${formatNumber(MAX_LOAN_AMOUNT)}`, {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    const termMonths = parseInt(formData.requestedTermMonths);
    if (termMonths < 1 || termMonths > MAX_LOAN_TERM_MONTHS) {
      toast.error(`Term must be between 1 and ${MAX_LOAN_TERM_MONTHS} months`, {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        requestedAmount: amount,
        loanType: formData.loanType,
        purpose: formData.purpose,
        requestedTermMonths: termMonths,
        collateralType: formData.collateralType,
      };

      const response = await api.post('/api/loans/apply', payload);

      if (response.data.success) {
        toast.success('Loan application submitted successfully!', {
          position: 'bottom-left',
          autoClose: 2500,
        });
        // Refresh applications list
        fetchApplications();
        // Reset form
        setFormData({
          requestedAmount: '',
          loanType: '',
          purpose: '',
          requestedTermMonths: '3',
          collateralType: 'CRYPTOCURRENCY',
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to submit loan application',
        {
          position: 'bottom-left',
          autoClose: 3000,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header with full-width background */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          width: '100%',
        }}
      >
        <Container
          fluid
          style={{
            maxWidth: isMobile ? '100%' : '85%',
            margin: '0 auto',
            padding: isMobile ? '0' : '0 1rem',
          }}
        >
          <div className='d-flex justify-content-between align-items-center p-3'>
            <Button
              variant='link'
              onClick={() => navigate(-1)}
              className='p-0'
              aria-label='Go back'
              style={{ color: '#FFFFFF' }}
            >
              <ArrowLeft size={24} />
            </Button>
            <h5 className='mb-0 fw-bold' style={{ color: '#FFFFFF' }}>
              Apply for Loan
            </h5>
            <div style={{ width: '24px' }}></div>
          </div>
        </Container>
      </div>

      {/* Main Content with constrained width */}
      <Container
        fluid
        style={{
          maxWidth: isMobile ? '100%' : '85%',
          margin: '0 auto',
          backgroundColor: '#FFFFFF',
          minHeight: '100vh',
          padding: isMobile ? '0' : '0 1rem',
        }}
      >
        {/* Content Section */}
        <div className='p-3 p-sm-4' style={{ paddingBottom: '100px' }}>
          <Card
            className='border-0 shadow-sm mb-4'
            style={{ backgroundColor: '#f0f4ff' }}
          >
            <Card.Body className='p-3'>
              <h6 className='fw-bold text-dark mb-2'>Loan Information</h6>
              <p className='text-muted small mb-0'>
                Submit your loan application by providing the required details
                below. Our team will review your application and get back to you
                within 24-48 hours.
              </p>
            </Card.Body>
          </Card>

          {/* Loan Application Form */}
          <Form onSubmit={handleSubmit}>
            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold text-dark'>
                Loan Amount (USDT) *
              </Form.Label>
              <Form.Control
                type='text'
                name='requestedAmount'
                placeholder={`Enter loan amount (${formatNumber(
                  MIN_LOAN_AMOUNT
                )} - ${formatNumber(MAX_LOAN_AMOUNT)})`}
                value={formData.requestedAmount}
                onChange={handleChange}
                onFocus={handleAmountFocus}
                onBlur={handleAmountBlur}
                disabled={loading}
                className='custom-placeholder'
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                }}
              />
              <Form.Text className='text-muted'>
                Min: {formatNumber(MIN_LOAN_AMOUNT)} USDT, Max:{' '}
                {formatNumber(MAX_LOAN_AMOUNT)} USDT
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold text-dark'>
                Loan Type *
              </Form.Label>
              <Form.Select
                name='loanType'
                value={formData.loanType}
                onChange={handleChange}
                disabled={loading}
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                }}
              >
                <option value=''>Select loan type</option>
                <option value='PERSONAL'>Personal</option>
                <option value='TRADING_CAPITAL'>Trading Capital</option>
                <option value='EMERGENCY'>Emergency</option>
                <option value='BUSINESS'>Business</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold text-dark'>
                Purpose *
              </Form.Label>
              <Form.Control
                as='textarea'
                name='purpose'
                rows={3}
                placeholder='Describe the purpose of your loan request (10-500 characters)'
                value={formData.purpose}
                onChange={handleChange}
                disabled={loading}
                maxLength={MAX_PURPOSE_LENGTH}
                className='custom-placeholder'
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  resize: 'vertical',
                }}
              />
              <Form.Text className='text-muted'>
                {formData.purpose.length}/{MAX_PURPOSE_LENGTH} characters (min:{' '}
                {MIN_PURPOSE_LENGTH})
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold text-dark'>
                Repayment Period *
              </Form.Label>
              <Form.Select
                name='requestedTermMonths'
                value={formData.requestedTermMonths}
                onChange={handleChange}
                disabled={loading}
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                }}
              >
                <option value='3'>3 Months</option>
                <option value='6'>6 Months</option>
                <option value='12'>12 Months</option>
                <option value='36'>36 Months</option>
                <option value='48'>48 Months</option>
                <option value='60'>60 Months</option>
              </Form.Select>
              <Form.Text className='text-muted'>
                Maximum term: {MAX_LOAN_TERM_MONTHS} months
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold text-dark'>
                Collateral Type *
              </Form.Label>
              <Form.Select
                name='collateralType'
                value={formData.collateralType}
                onChange={handleChange}
                disabled={true}
                className='no-dropdown-arrow'
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <option value='CRYPTOCURRENCY'>Cryptocurrency</option>
              </Form.Select>
            </Form.Group>

            {/* Terms Notice */}
            <div className='text-muted small p-3 bg-light rounded mb-3'>
              <p className='fw-bold text-dark mb-2'>Important Notes:</p>
              <p className='mb-2'>
                • Loan amount must be between $100 and $1,000,000
              </p>
              <p className='mb-2'>
                • Purpose must be between 10 and 500 characters
              </p>
              <p className='mb-2'>• Term must be between 3 and 60 months</p>
              <p className='mb-2'>
                • Interest rates will be determined based on your credit score
                and collateral value
              </p>
              <p className='mb-2'>
                • Collateral must be deposited before loan approval
              </p>
              <p className='mb-0'>
                • Early repayment options available with reduced interest
              </p>
            </div>

            <Button
              type='submit'
              disabled={loading}
              className='w-100 py-3 fw-semibold'
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? (
                <>
                  <Spinner
                    animation='border'
                    size='sm'
                    className='me-2'
                    role='status'
                  />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </Button>
          </Form>

          {/* Pending Applications Section */}
          <div className='mt-5'>
            <h6 className='fw-bold text-dark mb-3'>Your Loan Applications</h6>
            {applicationsLoading ? (
              <div className='text-center py-4'>
                <Spinner animation='border' style={{ color: '#667eea' }} />
              </div>
            ) : applications.length === 0 ? (
              <Card className='border-0 shadow-sm'>
                <Card.Body className='text-center py-4'>
                  <p className='text-muted mb-0'>No loan applications yet</p>
                </Card.Body>
              </Card>
            ) : (
              <div className='d-flex flex-column gap-3'>
                {applications.map((app) => (
                  <Card
                    key={app.id}
                    className='border-0 shadow-sm hover-card'
                    style={{ transition: 'all 0.3s ease' }}
                  >
                    <Card.Body className='p-3'>
                      <div className='d-flex justify-content-between align-items-start mb-2'>
                        <div>
                          <h6 className='fw-bold text-dark mb-1'>
                            {parseFloat(app.requestedAmount).toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}{' '}
                            USDT
                          </h6>
                          <span
                            className='badge small'
                            style={{
                              background:
                                app.status === 'PENDING'
                                  ? 'linear-gradient(45deg, #f59e0b, #d97706)'
                                  : app.status === 'APPROVED'
                                  ? 'linear-gradient(45deg, #00C851, #007E33)'
                                  : 'linear-gradient(45deg, #ef4444, #dc2626)',
                              color: 'white',
                              borderRadius: '12px',
                            }}
                          >
                            {app.status}
                          </span>
                        </div>
                        <div className='text-end'>
                          <small className='text-muted'>
                            {new Date(app.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                      </div>

                      <div className='row g-2 small'>
                        <div className='col-6'>
                          <span className='text-muted'>Loan Type:</span>
                          <br />
                          <span className='fw-semibold'>
                            {app.loanType.replace('_', ' ')}
                          </span>
                        </div>
                        <div className='col-6'>
                          <span className='text-muted'>Term:</span>
                          <br />
                          <span className='fw-semibold'>
                            {app.requestedTermMonths} months
                          </span>
                        </div>
                        <div className='col-6'>
                          <span className='text-muted'>Collateral:</span>
                          <br />
                          <span className='fw-semibold'>
                            {app.collateralType.replace('_', ' ')}
                          </span>
                        </div>
                        <div className='col-6'>
                          <span className='text-muted'>Credit Score:</span>
                          <br />
                          <span className='fw-semibold'>
                            {app.creditScoreAtTime}
                          </span>
                        </div>
                      </div>

                      {app.purpose && (
                        <div className='mt-2 pt-2 border-top'>
                          <small className='text-muted'>Purpose:</small>
                          <p
                            className='mb-0 small'
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {app.purpose}
                          </p>
                        </div>
                      )}

                      {app.rejectionReason && (
                        <div
                          className='mt-2 p-2 small rounded'
                          style={{ backgroundColor: '#fef2f2' }}
                        >
                          <strong className='text-danger'>
                            Rejection Reason:
                          </strong>
                          <br />
                          {app.rejectionReason}
                        </div>
                      )}

                      {app.approvalNotes && (
                        <div
                          className='mt-2 p-2 small rounded'
                          style={{ backgroundColor: '#f0fdf4' }}
                        >
                          <strong className='text-success'>
                            Approval Notes:
                          </strong>
                          <br />
                          {app.approvalNotes}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!applicationsLoading && applications.length > 0 && (
              <Card className='border-0 shadow-sm mt-4'>
                <Card.Body className='p-3'>
                  <div className='d-flex flex-column-reverse flex-sm-row justify-content-between align-items-center gap-2 gap-sm-3'>
                    <div className='text-muted small'>
                      Showing {applications.length} of {pagination.total}{' '}
                      Applications
                    </div>
                    <Pagination
                      currentPage={pagination.page}
                      totalPages={pagination.pages}
                      onPageChange={handlePageChange}
                      loading={applicationsLoading}
                    />
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        </div>
      </Container>

      <style jsx>{`
        .custom-placeholder::placeholder {
          color: #cbd5e0 !important;
          opacity: 1;
        }

        .custom-placeholder::-webkit-input-placeholder {
          color: #cbd5e0 !important;
          opacity: 1;
        }

        .custom-placeholder::-moz-placeholder {
          color: #cbd5e0 !important;
          opacity: 1;
        }

        .custom-placeholder:-ms-input-placeholder {
          color: #cbd5e0 !important;
          opacity: 1;
        }

        .no-dropdown-arrow {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: none !important;
        }

        .no-dropdown-arrow::-ms-expand {
          display: none;
        }

        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        @media (max-width: 576px) {
          h5 {
            font-size: 1.1rem !important;
          }
          h6 {
            font-size: 1rem !important;
          }
        }

        @media (max-width: 380px) {
          h5 {
            font-size: 1rem !important;
          }
          h6 {
            font-size: 0.95rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ApplyLoan;
